import ProductEvents from 'analytics/ProductEvents';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import get from 'lodash/get';
import { LoggerFactory } from 'logger';
import Service from 'model/Service';
import React from 'react';
import { Map as LeafletMap, Marker, TileLayer, Tooltip, ZoomControl } from 'react-leaflet';
import withAddLocation from '../hocs/withAddLocation';
import withAddMetro from '../hocs/withAddMetro';
import { withDisableServiceRegion, withEnableServiceRegion } from '../InfoPanel/children/ServiceRegionsView';
import ActiveMetro from './ActiveMetro';
import Clusterer from './Clusterer';
import ContextMenu from './ContextMenu';
import DirectConnection from './DirectConnection';
import ExportButton from './ExportButton';
import { getIcon } from './icons';
import MarkerGroup from './MarkerGroup';
import MetroConnections from './MetroConnections';
import PlatformEquinixOverlay from './PlatformEquinixOverlay';
import PhaseLocation from './PhaseLocation';
import './ProjectMap.scss';
import ServiceRegionMarker from './ServiceRegionMarker';
import { getLocations, getServiceRegions } from './utils';
import filterMetrosByType from 'utils/filterMetrosByType';
import { dismissAllToasts } from 'utils/Toast';
import cn from 'classnames';
import MarketplaceLayer from './MarketplaceLayer';
import MapKey from './MapKey';
import MetroInfoTag from './MetroInfoTag';
import HeatmapLayer from 'react-leaflet-heatmap-layer';
import MapButton from './MapButton';

const ConnectedServiceRegionMarker = withEnableServiceRegion(withDisableServiceRegion(ServiceRegionMarker));

const defaultBounds = new L.LatLngBounds([
  [47.613, -122.4821], // Seattle
  [60.1102, 24.7385], // Helsinki
  [-37.9716, 144.7729], // Melbourne
  [-27.3812, 152.713], // Brisbane
]);

// Empty map config
const defaultMapConfig = {
  maxZoom: 7,
  duration: 0.8,
  padding: [30, 30],
};

class ProjectMap extends React.Component {
  constructor(props) {
    super(props);
    this._logger = LoggerFactory.getLogger(this);
    this.state = {
      showButtons: true,
    };
  }

  componentDidMount() {
    if (this.map && this.map.leafletElement) {
      const bounds = this.getBoundsFromProps(this.props);
      this.map.leafletElement.createPane('tooltipPane').style.zIndex = 1800;
      if (bounds) {
        this.map.leafletElement.fitBounds(bounds, {
          maxZoom: 10,
          duration: 0.8,
          padding: [30, 30],
        });
      } else {
        this.map.leafletElement.fitBounds(defaultBounds, defaultMapConfig);
      }
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { selected, zoom, sectionMapFocus } = this.props;

    // we changed the selection
    const mapConfig = {
      maxZoom: 10,
      duration: 0.5,
      paddingTopLeft: [30, 30],
      paddingBottomRight: [360, 30],
    };

    if (
      this.map &&
      ((!selected && nextProps.selected) || (selected && !nextProps.selected) || (selected && nextProps.selected && !selected.id.equals(nextProps.selected.id)))
    ) {
      this.map.leafletElement.setMaxBounds([
        [90, -360],
        [-90, 360],
      ]);
      if (nextProps.selected && nextProps.selected.coordinates) {
        // We selested a location or a metro
        this.map.leafletElement.flyToBounds([nextProps.selected.coordinates.toLeaflet()], mapConfig);
      } else if (get(nextProps, 'selected.locations')) {
        // We selected a group
        const coordinates = nextProps.selected.locations.map((l) => l.coordinates.toLeaflet());
        if (coordinates.length) {
          // if the group isn't empty, move the map to show all locations
          const bounds = new L.LatLngBounds(coordinates);
          this.map.leafletElement.flyToBounds(bounds, mapConfig);
        }
      } else if (get(nextProps, 'selected.metroId.value')) {
        // We selected a service
        const activeMetro = nextProps.phase.metros.find((metro) => metro.id.value === nextProps.selected.metroId.value);
        const bounds = [activeMetro.coordinates.toLeaflet()];
        this.map.leafletElement.flyToBounds(bounds, mapConfig);
      } else {
        // we de-selected the last element, re-size the map to the whole net
        this.map.leafletElement.setMaxBounds([
          [90, -180],
          [-90, 180],
        ]);
        let bounds = this.getBoundsFromProps(nextProps) || defaultBounds;
        this.map.leafletElement.flyToBounds(bounds, {
          ...mapConfig,
          paddingTopLeft: [30, 30],
        });
      }
    }

    if (
      this.map &&
      ((!zoom && nextProps.zoom) || (zoom && !nextProps.zoom) || (zoom && nextProps.zoom && !zoom.coordinates.equals(nextProps.zoom.coordinates)))
    ) {
      this.map.leafletElement.flyToBounds([nextProps.zoom.coordinates.toLeaflet()], mapConfig);
    }

    if (this.map && sectionMapFocus !== nextProps.sectionMapFocus) {
      const bounds = this.getBoundsFromSectionFocus(nextProps.sectionMapFocus, nextProps);
      if (bounds) {
        this.map.leafletElement.flyToBounds(bounds, {
          ...mapConfig,
          paddingBottomRight: [30, 30],
        });
      }
    }
  }

  getBoundsFromProps = (props) => {
    const { phase } = props;
    const objectsInMetros = phase.metros.reduce((objects, metro) => {
      return [...objects, ...metro.locations, metro];
    }, []);
    const objectsInGroups = phase.groups.reduce((objects, group) => {
      return [...objects, ...group.locations];
    }, []);
    const objects = [...objectsInMetros, ...objectsInGroups, ...phase.unconnectedLocations];
    if (objects.length <= 0) {
      return null;
    }
    const positions = objects.map((o) => o.position || o.coordinates.toLeaflet());
    return new L.LatLngBounds(positions);
  };

  getBoundsFromSectionFocus = (sectionMapFocus, props) => {
    if (!sectionMapFocus || !props.phase) {
      return null;
    }

    if (sectionMapFocus.mode === 'all') {
      return this.getBoundsFromProps(props) || defaultBounds;
    }

    if (sectionMapFocus.mode === 'maxZoomOut') {
      return new L.LatLngBounds([
        [85, -180],
        [-85, 180],
      ]);
    }

    const metros = props.phase.metros || [];

    if (sectionMapFocus.mode === 'metroCode' && sectionMapFocus.metroCode) {
      const metro = metros.find((item) => item.code?.value === sectionMapFocus.metroCode);
      return metro?.coordinates ? new L.LatLngBounds([metro.coordinates.toLeaflet()]) : null;
    }

    if (sectionMapFocus.mode === 'metroCodes' && Array.isArray(sectionMapFocus.metroCodes)) {
      const positions = metros
        .filter((item) => sectionMapFocus.metroCodes.includes(item.code?.value))
        .map((item) => item.coordinates?.toLeaflet())
        .filter((position) => !!position);
      return positions.length ? new L.LatLngBounds(positions) : null;
    }

    return null;
  };

  _onSelect = (type, selectedItem) => {
    const { isEditing, isLite, onSelect, onClose } = this.props;
    if (isLite || !isEditing) return;
    onClose();
    onSelect(type, selectedItem);
  };

  _addMetro = async (phaseId, metro) => {
    const { projectId, addMetro, isEditing, isLite } = this.props;
    if (isLite || !isEditing) return;

    this._onSelect();
    await addMetro(projectId, phaseId, metro);
    dismissAllToasts();
    ProductEvents.metroAdded(metro.name);
  };

  _contextMenu = (e) => {
    if (this.props.platform) return;
    const { isEditing, isLite } = this.props;
    if (isLite || !isEditing) return;
    this.setState({ contextMenuPosition: e.latlng });
  };

  _contextMenuClose = () => {
    this.setState({ contextMenuPosition: null });
  };

  _createLocation = (type, position) => async () => {
    this.setState({ contextMenuPosition: null });
    const { phase, projectId, addLocation } = this.props;
    const query = `lat=${position.lat}&lon=${position.lng}&format=json`;
    const response = await fetch(`https://nominatim.openstreetmap.org/reverse?${query}`);
    const address = await response.json();
    this._logger.debug(`Adding location via right-click on the map.\nAddress: ${address.display_name}`);
    await addLocation(projectId, phase.id, type, address, position);
    dismissAllToasts();
    ProductEvents.locationAdded(type.toString());
  };

  _onManageMetros = () => {
    const { onManageMetros } = this.props;
    this.setState({ contextMenuPosition: null });
    onManageMetros();
  };

  _onViewportChange = (viewport) => {
    let iconSize = 1;
    if (viewport.zoom < 5) iconSize = 0.9;
    if (viewport.zoom < 4.5) iconSize = 0.8;
    if (viewport.zoom < 4) iconSize = 0.7;
    if (viewport.zoom < 3.5) iconSize = 0.6;
    if (viewport.zoom < 3) iconSize = 0.5;
    if (viewport.zoom < 2.5) iconSize = 0.4;
    if (viewport.zoom < 2) iconSize = 0.3;
    if (viewport.zoom < 1.5) iconSize = 0.2;

    if (iconSize !== this.state.iconSize) {
      this.setState({ iconSize });
    }
  };

  onMetalMetroSelected = (metro) => {
    const mapConfig = {
      maxZoom: 30,
      duration: 0.5,
      paddingTopLeft: [30, 30],
      paddingBottomRight: [30, 30],
    };

    const positions = [metro.coordinates.toLeaflet()];

    this.map.leafletElement.flyToBounds(positions, {
      ...mapConfig,
    });
  };

  componentDidUpdate(prev) {
    const { isSidebarCollapsed, isInfoOpen } = this.props;
    if (prev.isSidebarCollapsed !== isSidebarCollapsed || prev.isInfoOpen !== isInfoOpen) {
      setTimeout(() => {
        this.map.leafletElement.invalidateSize({ pan: true, animate: true });
      }, 251);
    }
  }

  getMapZoom = () => this.map?.leafletElement.getZoom();

  render() {
    const {
      projectId,
      phaseId,
      project,
      phase,
      selected,
      isInfoOpen,
      metros = [],
      connectionType,
      layers = {},
      loading,
      ringLatency,
      ringColor,
      metroType,
      onMetroPanelTabChange,
      platform,
      platformMetros,
      product,
      onClose,
      isSidebarCollapsed,
      mapType,
      marketplaceSegment,
      isEditing,
      isLite,
      infraComponentCard,
      cloudsList,
      selectedOnRamps,
      onRampConfiguration,
      capacityConfiguration,
      capOnRamp,
      isCapacityAllowed,
      selectedLayer,
      setSelectedLayer,
      mapUrl,
      selectedMapStyle,
      defaultLayerConfiguration,
      defaultCapacityConfiguration,
      selectedEms,
    } = this.props;

    const safeMetros = Array.isArray(metros) ? metros : [];
    const effectiveIsEditing = !!isEditing && !isLite;
    const safeCloudsList = Array.isArray(cloudsList) ? cloudsList : [];
    const safeSelectedOnRamps = Array.isArray(selectedOnRamps) ? selectedOnRamps : [];
    const locations = getLocations(phase, layers);
    const regions = getServiceRegions(phase, selected);
    const activeMetros = filterMetrosByType(phase.metros, metroType);
    const safeActiveMetros = Array.isArray(activeMetros) ? activeMetros : [];
    const directConnections = phase.directConnections;
    const { iconSize, contextMenuPosition, showButtons } = this.state;
    const layersOptions = [
      ...(isCapacityAllowed
        ? [{ value: 'capacity', label: 'Capacity', isActive: JSON.stringify(capacityConfiguration) !== JSON.stringify(defaultCapacityConfiguration) }]
        : []),
      ...(!!safeCloudsList.length ? [{ value: 'onRamps', label: 'Cloud On-Ramps', isActive: !!safeSelectedOnRamps.length }] : []),
      { value: 'marketplace', label: 'Ecosystem Density', isActive: !!marketplaceSegment },
      { value: 'layers', label: 'Map Layers', isActive: JSON.stringify(layers) !== JSON.stringify(defaultLayerConfiguration) },
      { value: 'ems', label: 'EMS', isActive: !!selectedEms },
    ];

    const maxBounds = isInfoOpen
      ? [
          [90, -180],
          [-90, 334],
        ]
      : [
          [90, -180],
          [-90, 180],
        ];
    const getOnRampPoints = () => {
      const filteredMetros = safeMetros?.filter((metro) => metro.onRampClouds.some((cloud) => safeSelectedOnRamps.includes(cloud.cloud)));
      return filteredMetros?.map((metro) => {
        const filtered = metro.onRampClouds.filter((cloud) => safeSelectedOnRamps.includes(cloud.cloud));
        const allCount = filtered.reduce((sum, { eqxCenters }) => sum + eqxCenters, 0);
        const allMarketShare = filtered.reduce((sum, cloud) => sum + cloud.marketShare, 0) / filtered.length;
        return [allCount, allMarketShare.toFixed(1), metro.code, metro.coordinates.latitude, metro.coordinates.longitude];
      });
    };
    const getMaxValue = (points) => {
      const values = points?.map((point) => point[0]);
      return Math.max(...values);
    };

    const getCapacity = (capacity) => {
      if (!capacity) return capacity;

      const fieldsToCheck = ['cabETotal', 'capacityAvailableMax', 'capacityTotal', 'capacityAvailable', 'capacityAverage'];

      const changed = { ...capacity };

      fieldsToCheck.forEach((key) => {
        if (changed[key] === -1) {
          changed[key] = 'N/A';
        }
      });

      return changed;
    };

    const onRampPoints = getOnRampPoints();
    const onRampMaxValue = onRampPoints ? getMaxValue(onRampPoints) : null;

    const tagList =
      capOnRamp === 'all'
        ? safeMetros
        : capOnRamp === 'active'
        ? safeMetros.filter((metro) => !!safeActiveMetros.find((m) => m.code.value === metro.code))
        : safeMetros.filter((metro) => !safeActiveMetros.find((m) => m.code.value === metro.code));
    const zoom = this.getMapZoom();

    return (
      <section className={cn('map', { isSidebarCollapsed, selectedLayer, 'panel-open': isInfoOpen, isLite })}>
        {!isInfoOpen && <MapKey />}
        <LeafletMap
          data-testid={'map'}
          className={cn({ 'old-map-background': selectedMapStyle === 'oldMapStyle' })}
          animate
          useFlyTo
          attributionControl={false}
          zoomControl={false}
          boundsOptions={{
            maxZoom: 10,
            duration: 0.8,
          }}
          minZoom={2}
          maxZoom={10}
          maxBounds={maxBounds}
          onContextMenu={this._contextMenu}
          onViewportChanged={this._onViewportChange}
          ref={(m) => (this.map = m)}>
          {!isLite && <ZoomControl position="bottomright" />}
          <TileLayer
            url={mapUrl}
            attribution={
              '<a href="https://www.maptiler.com/copyright/" target="_blank">© MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">© OpenStreetMap contributors</a>'
            }
            bounds={maxBounds}
            noWrap
            tileSize={selectedMapStyle === 'newMapStyle' ? 512 : 256}
            zoomOffset={selectedMapStyle === 'newMapStyle' ? -1 : 0}
          />
          {!isLite && (
            <>
              <MapButton
                data-testid={'options-button'}
                className={cn('options-button', { open: showButtons })}
                text={!showButtons ? 'Map & Data Layers' : ''}
                position={'topright'}
                onClick={() => this.setState({ showButtons: !showButtons })}
              />
              {showButtons &&
                layersOptions.map((option) => (
                  <MapButton
                    key={option.value}
                    data-testid={`${option.value}-button`}
                    className={cn(`${option.value}-button`, { open: selectedLayer?.value === option.value, active: option.isActive })}
                    text={option.label}
                    position={'topright'}
                    onClick={() => {
                      if (selectedLayer?.value === option.value) setSelectedLayer(null);
                      else setSelectedLayer(option);
                    }}
                  />
                ))}
              <ExportButton
                project={project}
                phase={phase}
                connectionType={connectionType}
                layers={layers}
                ringColor={ringColor}
                ringLatency={ringLatency}
                marketplaceSegment={marketplaceSegment}
                mapType={mapType}
                selectedOnRamps={selectedOnRamps}
                onRampConfiguration={onRampConfiguration}
                capacityConfiguration={capacityConfiguration}
                capOnRamp={capOnRamp}
                selectedEms={selectedEms}
              />{' '}
            </>
          )}
          {contextMenuPosition && (
            <ContextMenu onClose={this._contextMenuClose} position={contextMenuPosition}>
              <button onClick={this._createLocation('OFFICE', contextMenuPosition)}>Add a Customer Site</button>
              <button onClick={this._createLocation('DATACENTER', contextMenuPosition)}>Add a Customer Data Center</button>
              <button onClick={this._onManageMetros}>Manage Metros</button>
            </ContextMenu>
          )}
          {mapType && <MarketplaceLayer mapType={mapType} marketplaceSegment={marketplaceSegment} metros={safeMetros} zoom={zoom} />}
          {onRampConfiguration.showHeatMap && (
            <HeatmapLayer
              points={onRampPoints}
              longitudeExtractor={(m) => m[4]}
              latitudeExtractor={(m) => m[3]}
              intensityExtractor={(m) => parseFloat(m[0])}
              radius={zoom <= 2 ? 20 : zoom > 2 && zoom < 3.2 ? 28 : zoom >= 3.2 && zoom < 4.4 ? 40 : 50}
              max={onRampMaxValue}
              maxZoom={1}
              blur={20}
              minOpacity={0}
              gradient={{
                0.4: 'blue',
                0.8: 'yellow',
                1.0: 'red',
              }}
            />
          )}
          {tagList?.map((metro) => {
            const capacity = metro.capacity;
            const onRamp = onRampPoints.find((ramp) => ramp[2] === metro.code);
            const showEms = !!metro.emsData && !!selectedEms ? metro.emsData[selectedEms] : false;
            if (
              (!!safeSelectedOnRamps.length && onRamp && (onRampConfiguration.showCount || onRampConfiguration.showMarketShare)) ||
              (JSON.stringify(capacityConfiguration) !==
                JSON.stringify({
                  datacenters: false,
                  cabe: null,
                  kva: null,
                  cooling: false,
                  metro: 'total',
                  cabinet: 'average',
                }) &&
                capacity) ||
              !!showEms
            ) {
              return (
                <MetroInfoTag
                  key={metro.code}
                  capacity={getCapacity(capacity)}
                  position={[metro.coordinates.latitude, metro.coordinates.longitude]}
                  onRamp={onRamp}
                  onRampConfiguration={onRampConfiguration}
                  capacityConfiguration={capacityConfiguration}
                  iconSize={iconSize}
                  showEms={!!showEms}
                />
              );
            } else return null;
          })}

          {layers.inactiveMetros &&
            safeMetros?.map((metro) => {
              return (
                <Marker
                  key={metro.id}
                  position={metro.coordinates.toLeaflet()}
                  icon={getIcon('metro', iconSize)}
                  onClick={() => this._addMetro(phaseId, metro)}
                  zIndexOffset={200}>
                  {!loading && (
                    <Tooltip pane={'tooltipPane'} direction="top" offset={[0, -5]}>
                      {!safeActiveMetros.find((am) => am.id.equals(metro.id)) && 'Enable'} {metro.name}
                    </Tooltip>
                  )}
                </Marker>
              );
            })}
          {layers.customerLocations &&
            phase.sortedGroups.map(([color, locations]) => (
              <MarkerGroup key={color} data={locations.map((l) => l.coordinates.toLeaflet())} color={color} iconSize={iconSize} />
            ))}
          {safeActiveMetros.map((metro) => {
            return (
              <ActiveMetro
                key={metro.id}
                projectId={project.id}
                phaseId={phaseId}
                metro={metro}
                selected={selected}
                onSelect={this._onSelect}
                iconSize={iconSize}
                layers={layers}
                noText={!layers.latencies}
                ringLatency={ringLatency}
                ringColor={ringColor}
                onMetroPanelTabChange={onMetroPanelTabChange}
                onClose={onClose}
                marketplaceSegment={marketplaceSegment}
                mapType={mapType}
                isEditing={effectiveIsEditing}
                infraComponentCard={infraComponentCard}
                zoom={this.getMapZoom()}
              />
            );
          })}
          {platform && (
            <PlatformEquinixOverlay
              platformMetros={platformMetros}
              projectId={project.id}
              phaseId={phaseId}
              onSelect={this.onMetalMetroSelected}
              iconSize={iconSize}
              product={product}
              platform={platform}
            />
          )}
          <Clusterer
            key={`cluster-${phaseId}-${this.getMapZoom()}-${layers.customerLocations}`}
            iconSize={iconSize}
            projectId={project.id}
            phaseId={phaseId}
            customPaneZIndex={1000}>
            {layers.customerLocations &&
              locations.map((location) => (
                <PhaseLocation
                  key={location.id}
                  projectId={project.id}
                  phaseId={phaseId}
                  location={location}
                  selected={selected}
                  onSelect={this._onSelect}
                  iconSize={iconSize}
                  onClose={onClose}
                  isEditing={effectiveIsEditing}
                  locationLabels={layers.locationLabels && layers.labelSectionEnabled}
                />
              ))}
          </Clusterer>
          {regions
            .filter((region) => !region.region.deprecated || (region.region.deprecated && region.enabled))
            .map((spec) => (
              <ConnectedServiceRegionMarker
                projectId={projectId}
                phaseId={phaseId}
                service={spec.service}
                key={spec.region.id}
                region={spec.region}
                selectedItem={selected}
                selected={spec.selected}
                enabled={spec.enabled}
                connections={spec.connections}
                metros={safeMetros}
                layers={layers}
                iconSize={iconSize}
              />
            ))}
          {selected instanceof Service &&
            selected.regions.length > 0 &&
            selected.regions.map((region) => (
              <ServiceRegionMarker
                service={selected}
                key={region.id}
                region={region}
                enabled={this.props.selectedRegions.find((r) => r.equals(region))}
                onEnableRegion={this.props.onEnableRegion}
                onDisableRegion={this.props.onDisableRegion}
                layers={layers}
                iconSize={iconSize}
              />
            ))}
          {layers.connections && (
            <MetroConnections
              noText={!layers.latencies}
              connectionType={connectionType}
              metros={safeMetros}
              activeMetros={safeActiveMetros}
              iconSize={iconSize}
            />
          )}
          {layers.directConnections &&
            directConnections.map((dm) => {
              const fromMetro = dm.source;
              return dm.connectedTo.map((dc) => {
                return (
                  <DirectConnection
                    key={`${fromMetro} => ${dc.metro}`}
                    fromMetro={fromMetro}
                    toMetro={dc.metro}
                    text={dc.label}
                    color={dc.color}
                    iconSize={iconSize}
                  />
                );
              });
            })}
        </LeafletMap>
        {loading && (
          <div className="saving-toast">
            <div className="spinner"></div>
            <p>Updating...</p>
          </div>
        )}
      </section>
    );
  }
}

ProjectMap = withAddMetro(ProjectMap);
ProjectMap = withAddLocation(ProjectMap);

export default ProjectMap;
